import { useEffect, useRef, useState } from 'react';
import { PublicClientApplication, EventType } from '@azure/msal-browser';

// Iframe / Teams detection — used to switch from loginRedirect to loginPopup
// (Microsoft blocks loginRedirect inside iframes for security: redirect_in_iframe error)
const IS_IN_IFRAME = (() => {
  try { return window.self !== window.top; } catch { return true; }
})();

// Try to detect Teams specifically (host name in URL or referrer)
const IS_IN_TEAMS = (() => {
  try {
    const ref = document.referrer || '';
    const ancestor = (window.location.ancestorOrigins && window.location.ancestorOrigins[0]) || '';
    return /teams\.microsoft\.com|teams\.live\.com|outlook\.office\.com/i.test(ref + ' ' + ancestor);
  } catch { return false; }
})();

// MSAL configuration — uses .env values
const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_MS_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_MS_TENANT_ID}`,
    redirectUri: process.env.REACT_APP_MS_REDIRECT_URI || window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    allowNativeBroker: false,
  },
};

const loginRequest = {
  scopes: ['User.Read', 'openid', 'profile', 'email'],
  prompt: 'select_account',
};

// Hand the signed-in account to the portal and open it. The account goes in
// this tab's sessionStorage (read by MindOps.html), never in the URL: a URL
// with ?user=&email= let anyone open the portal as anyone by typing an email.
// `mo_return` is the page MindOps.html was on when it sent the user here to
// sign in, so they land back on it.
function enterPortal(name, email, via) {
  try {
    sessionStorage.setItem('mo_session', JSON.stringify({ name, email, via, at: Date.now() }));
  } catch (e) {
    console.error('[Auth] Could not store the session:', e?.message);
  }
  let back = '';
  try {
    back = sessionStorage.getItem('mo_return') || '';
    sessionStorage.removeItem('mo_return');
  } catch (e) { /* no return page */ }
  window.location.replace('/MindOps.html' + (back.startsWith('?') ? back : ''));
}

// Module-level singletons — survive React StrictMode double-invocation
let msalInstance = null;
let msalReadyPromise = null;

function getMsalInstance() {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
}

// One-time init + redirect handling — completes in module scope
function initMsal() {
  if (msalReadyPromise) return msalReadyPromise;

  msalReadyPromise = (async () => {
    console.log('[MSAL] === INIT START ===');
    console.log('[MSAL] URL on load:', window.location.href);
    console.log('[MSAL] In iframe:', IS_IN_IFRAME, '| In Teams:', IS_IN_TEAMS);

    // ============================================================
    // TEAMS SSO PATH — silent token acquisition, no popup, no redirect
    // ============================================================
    if (IS_IN_IFRAME) {
      try {
        console.log('[Teams SSO] Attempting silent SSO via Teams JS SDK...');
        const teamsJs = await import('@microsoft/teams-js');
        const { app, authentication } = teamsJs;
        await app.initialize();
        const ctx = await app.getContext();
        console.log('[Teams SSO] Context loaded, host:', ctx?.app?.host?.name);

        // First try: get token from Teams (requires manifest webApplicationInfo)
        try {
          const token = await authentication.getAuthToken();
          console.log('[Teams SSO] ✅ Got token from Teams');
          // JWT decode (no validation needed — backend will validate)
          const payload = JSON.parse(atob(token.split('.')[1]));
          const name = payload.name || ctx?.user?.displayName || 'User';
          const email = payload.preferred_username || payload.upn || ctx?.user?.userPrincipalName || '';
          if (email) {
            enterPortal(name, email, 'teams');
            return { redirecting: true };
          }
        } catch (tokenErr) {
          console.warn('[Teams SSO] getAuthToken failed:', tokenErr?.message || tokenErr);
          // Fall back to using context-only user info if available
          if (ctx?.user?.userPrincipalName) {
            const name = ctx.user.displayName || ctx.user.userPrincipalName;
            const email = ctx.user.userPrincipalName;
            console.log('[Teams SSO] ✅ Using Teams context user info (no token needed)');
            enterPortal(name, email, 'teams');
            return { redirecting: true };
          }
        }
      } catch (e) {
        console.warn('[Teams SSO] SDK init failed, falling back to MSAL:', e?.message);
      }
    }

    // ============================================================
    // STANDARD MSAL PATH — for normal browser context
    // ============================================================
    // CAPTURE hash BEFORE initialize (which may clean it)
    const initialHash = window.location.hash;
    const initialSearch = window.location.search;
    const hadAuthResponse = (initialHash.includes('code=') || initialHash.includes('error=') ||
                            initialSearch.includes('code=') || initialSearch.includes('error='));
    console.log('[MSAL] Captured hash before init:', initialHash.slice(0, 60));
    console.log('[MSAL] Had auth response in URL:', hadAuthResponse);

    const instance = getMsalInstance();
    await instance.initialize();
    console.log('[MSAL] initialize() done');

    // Process redirect response
    try {
      console.log('[MSAL] Calling handleRedirectPromise...');
      const response = await instance.handleRedirectPromise();
      console.log('[MSAL] handleRedirectPromise result:', response ? 'RESPONSE' : 'null');
      if (response && response.account) {
        console.log('[MSAL] ✅ Got response account:', response.account.username);
      }
    } catch (e) {
      console.error('[MSAL] ❌ handleRedirectPromise error:', e.errorCode, e.errorMessage);
    }

    // Poll for accounts up to 10 seconds (MSAL async storage may take time, esp. on prod CDN)
    let accounts = instance.getAllAccounts();
    console.log('[MSAL] Initial getAllAccounts() count:', accounts.length);

    if (accounts.length === 0 && hadAuthResponse) {
      console.log('[MSAL] Auth was in URL but no accounts yet — polling...');
      for (let i = 0; i < 100; i++) {
        await new Promise(r => setTimeout(r, 100));
        accounts = instance.getAllAccounts();
        if (accounts.length > 0) {
          console.log(`[MSAL] ✅ Accounts found after ${(i+1)*100}ms`);
          break;
        }
      }
      if (accounts.length === 0) {
        console.warn('[MSAL] Polling timeout — accounts never populated. Trying ssoSilent...');
        try {
          const silentResult = await instance.ssoSilent({ scopes: ['User.Read'] });
          console.log('[MSAL] ssoSilent succeeded:', silentResult.account?.username);
          instance.setActiveAccount(silentResult.account);
          accounts = instance.getAllAccounts();
        } catch (e) {
          console.warn('[MSAL] ssoSilent failed:', e.errorCode);
        }
      }
    }

    // Even if no auth in URL, try ssoSilent (silent SSO from Microsoft session)
    // Fast 2-sec timeout so login page shows quickly if no MS session exists
    if (accounts.length === 0 && !hadAuthResponse) {
      console.log('[MSAL] No accounts and no URL auth — attempting ssoSilent (2s timeout)...');
      try {
        const silentResult = await Promise.race([
          instance.ssoSilent({ scopes: ['User.Read'] }),
          new Promise((_, rej) => setTimeout(() => rej(new Error('ssoSilent timeout')), 2000)),
        ]);
        if (silentResult && silentResult.account) {
          instance.setActiveAccount(silentResult.account);
          accounts = instance.getAllAccounts();
          console.log('[MSAL] ✅ ssoSilent restored session');
        }
      } catch (e) {
        // No active Microsoft session — show login (normal first visit)
        console.log('[MSAL] ssoSilent skipped (no MS session or timeout)');
      }
    }

    if (accounts.length > 0) {
      const a = accounts[0];
      const name = a.name || a.username || 'User';
      const email = a.username || a.idTokenClaims?.email || '';
      console.log('[MSAL] ✅ Redirecting to portal:', email);
      enterPortal(name, email, 'microsoft');
      return { redirecting: true };
    }

    console.log('[MSAL] === No accounts — show login page ===');
    return { redirecting: false };
  })();

  return msalReadyPromise;
}

export default function App() {
  const canvasRef = useRef(null);
  const logoTiltRef = useRef(null);
  const btnRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999, nx: 0.5, ny: 0.5 });
  const smoothMouseRef = useRef({ nx: 0.5, ny: 0.5 });
  const ripplesRef = useRef([]);
  // Skip "Verifying session" if no auth code in URL — show login page instantly
  const initialHasAuth = (() => {
    try {
      const h = window.location.hash || '';
      const s = window.location.search || '';
      return h.includes('code=') || h.includes('error=') || s.includes('code=') || s.includes('error=');
    } catch { return false; }
  })();
  const [authState, setAuthState] = useState(initialHasAuth ? 'checking' : 'ready');
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams(window.location.search);

    // SIGNOUT FLOW
    if (params.get('signout') === '1') {
      setAuthState('signing-out');
      (async () => {
        try {
          const instance = getMsalInstance();
          await instance.initialize();
          const acc = instance.getAllAccounts()[0];
          try { instance.clearCache(); } catch (e) {}
          if (acc) {
            await instance.logoutRedirect({
              account: acc,
              postLogoutRedirectUri: window.location.origin,
            });
          } else {
            window.location.replace('/');
          }
        } catch (err) {
          console.error('Signout error:', err);
          window.location.replace('/');
        }
      })();
      return;
    }

    // NORMAL FLOW
    initMsal().then((result) => {
      if (cancelled) return;

      // initMsal handles all redirects internally — if redirecting, stay in checking
      if (result?.redirecting) {
        return;
      }

      // No session — show login UI
      setAuthState('ready');
    }).catch((err) => {
      if (cancelled) return;
      console.error('MSAL init error:', err);
      setErrorMsg(err.errorMessage || err.message || 'Authentication error');
      setAuthState('ready');
    });

    return () => { cancelled = true; };
  }, []);

  // Sign-in handler
  const handleSignIn = async () => {
    console.log('[MSAL] Sign in clicked. In iframe:', IS_IN_IFRAME);
    setAuthState('signing-in');
    setErrorMsg(null);
    try {
      await initMsal();
      const instance = getMsalInstance();

      if (IS_IN_IFRAME) {
        // IFRAME / TEAMS: use popup (loginRedirect blocked inside iframes)
        console.log('[MSAL] Using loginPopup (iframe context)');
        const result = await instance.loginPopup(loginRequest);
        if (result && result.account) {
          console.log('[MSAL] ✅ Popup success:', result.account.username);
          instance.setActiveAccount(result.account);
          const a = result.account;
          const name = a.name || a.username || 'User';
          const email = a.username || a.idTokenClaims?.email || '';
          enterPortal(name, email, 'microsoft');
          return;
        }
        // No account from popup — show error
        throw new Error('Sign-in cancelled or failed');
      } else {
        // BROWSER: use redirect (best UX outside iframes)
        console.log('[MSAL] Using loginRedirect (browser context)');
        await instance.loginRedirect(loginRequest);
      }
    } catch (err) {
      console.error('[MSAL] Sign in failed:', err);
      console.error('[MSAL] Error code:', err.errorCode);
      console.error('[MSAL] Error message:', err.errorMessage);
      // Friendlier message for popup blocked
      let msg = err.errorMessage || err.message || 'Sign in failed';
      if (err.errorCode === 'popup_window_error' || /popup/i.test(msg)) {
        msg = 'Popup blocked — please allow popups for this site and try again';
      }
      setErrorMsg(msg);
      setAuthState('ready');
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf;

    const setSize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    setSize();

    const PARTICLE_COUNT = 80;
    const particles = [];
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      particles.push({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        size: Math.random() * 1.5 + 0.4,
        depth: Math.random(),
        pulsePhase: Math.random() * Math.PI * 2,
        isAccent: Math.random() < 0.15,
      });
    }

    const blobs = [
      { x: 0.15, y: 0.25, color: [0, 80, 140], radius: 700, phase: 0, speed: 0.00025 },
      { x: 0.85, y: 0.75, color: [230, 98, 8], radius: 550, phase: Math.PI, speed: 0.00035, weight: 0.7 },
      { x: 0.55, y: 0.5, color: [60, 120, 220], radius: 800, phase: 1, speed: 0.0002 },
      { x: 0.75, y: 0.15, color: [160, 80, 220], radius: 500, phase: 2, speed: 0.0003, weight: 0.5 },
      { x: 0.2, y: 0.85, color: [220, 60, 140], radius: 450, phase: 3, speed: 0.00028, weight: 0.4 },
    ];

    const t0 = performance.now();

    const animate = () => {
      const now = performance.now();
      const elapsed = now - t0;
      const W = window.innerWidth;
      const H = window.innerHeight;
      const mouse = mouseRef.current;
      const smooth = smoothMouseRef.current;

      smooth.nx += (mouse.nx - smooth.nx) * 0.05;
      smooth.ny += (mouse.ny - smooth.ny) * 0.05;

      const baseGrad = ctx.createRadialGradient(W * smooth.nx, H * smooth.ny, 0, W / 2, H / 2, Math.max(W, H));
      baseGrad.addColorStop(0, '#0A1628');
      baseGrad.addColorStop(0.5, '#050B1A');
      baseGrad.addColorStop(1, '#000510');
      ctx.fillStyle = baseGrad;
      ctx.fillRect(0, 0, W, H);

      blobs.forEach((b) => {
        const wobbleX = Math.sin(elapsed * b.speed + b.phase) * 0.18;
        const wobbleY = Math.cos(elapsed * b.speed * 1.3 + b.phase) * 0.14;
        const px = (b.x + wobbleX) * W + (smooth.nx - 0.5) * 60;
        const py = (b.y + wobbleY) * H + (smooth.ny - 0.5) * 60;
        const rW = b.radius * (0.85 + Math.sin(elapsed * b.speed * 2) * 0.15);
        const w = b.weight || 0.65;

        const grad = ctx.createRadialGradient(px, py, 0, px, py, rW);
        grad.addColorStop(0, `rgba(${b.color[0]}, ${b.color[1]}, ${b.color[2]}, ${0.5 * w})`);
        grad.addColorStop(0.4, `rgba(${b.color[0]}, ${b.color[1]}, ${b.color[2]}, ${0.22 * w})`);
        grad.addColorStop(1, `rgba(${b.color[0]}, ${b.color[1]}, ${b.color[2]}, 0)`);
        ctx.globalCompositeOperation = 'screen';
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, W, H);
      });
      ctx.globalCompositeOperation = 'source-over';

      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = W;
        if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H;
        if (p.y > H) p.y = 0;

        const parallaxX = (smooth.nx - 0.5) * 40 * p.depth;
        const parallaxY = (smooth.ny - 0.5) * 40 * p.depth;
        const x = p.x - parallaxX;
        const y = p.y - parallaxY;

        const breath = Math.sin(elapsed * 0.001 + p.pulsePhase) * 0.4 + 0.6;
        const opacity = (0.3 + p.depth * 0.5) * breath;
        const r = p.size * (0.7 + p.depth * 0.6);

        if (p.isAccent) {
          const glow = ctx.createRadialGradient(x, y, 0, x, y, r * 6);
          glow.addColorStop(0, `rgba(255, 160, 80, ${opacity * 0.8})`);
          glow.addColorStop(0.4, `rgba(230, 98, 8, ${opacity * 0.3})`);
          glow.addColorStop(1, 'rgba(230, 98, 8, 0)');
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.arc(x, y, r * 6, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = `rgba(255, 200, 140, ${opacity})`;
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fill();
        } else {
          const glow = ctx.createRadialGradient(x, y, 0, x, y, r * 4);
          glow.addColorStop(0, `rgba(180, 220, 255, ${opacity * 0.5})`);
          glow.addColorStop(1, 'rgba(180, 220, 255, 0)');
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.arc(x, y, r * 4, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = `rgba(220, 235, 255, ${opacity * 0.9})`;
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fill();
        }
      });

      if (mouse.x > -1000) {
        const cg = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, 400);
        cg.addColorStop(0, 'rgba(230, 98, 8, 0.14)');
        cg.addColorStop(0.4, 'rgba(80, 140, 220, 0.07)');
        cg.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = cg;
        ctx.fillRect(mouse.x - 400, mouse.y - 400, 800, 800);
      }

      ripplesRef.current = ripplesRef.current.filter((r) => {
        const age = (now - r.born) / 1000;
        if (age > 1.6) return false;
        const radius = age * 420;
        const op = Math.max(0, 1 - age / 1.6) * 0.55;

        ctx.strokeStyle = `rgba(230, 98, 8, ${op})`;
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.arc(r.x, r.y, radius, 0, Math.PI * 2);
        ctx.stroke();

        ctx.strokeStyle = `rgba(150, 200, 255, ${op * 0.7})`;
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.arc(r.x, r.y, radius * 0.65, 0, Math.PI * 2);
        ctx.stroke();

        const ig = ctx.createRadialGradient(r.x, r.y, 0, r.x, r.y, radius * 0.7);
        ig.addColorStop(0, `rgba(230, 98, 8, ${op * 0.25})`);
        ig.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = ig;
        ctx.beginPath();
        ctx.arc(r.x, r.y, radius * 0.7, 0, Math.PI * 2);
        ctx.fill();

        return true;
      });

      raf = requestAnimationFrame(animate);
    };
    animate();

    const handleMove = (e) => {
      const m = mouseRef.current;
      m.x = e.clientX;
      m.y = e.clientY;
      m.nx = e.clientX / window.innerWidth;
      m.ny = e.clientY / window.innerHeight;

      const mxOff = m.nx - 0.5;
      const myOff = m.ny - 0.5;

      if (logoTiltRef.current) {
        logoTiltRef.current.style.transform = `rotateX(${-myOff * 10}deg) rotateY(${mxOff * 10}deg)`;
      }

      if (btnRef.current) {
        const r = btnRef.current.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const dx = e.clientX - cx;
        const dy = e.clientY - cy;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d < 160) {
          const f = (1 - d / 160) * 0.25;
          btnRef.current.style.transform = `translate3d(${dx * f}px, ${dy * f}px, 0)`;
        } else {
          btnRef.current.style.transform = 'translate3d(0,0,0)';
        }
      }
    };

    const handleClick = (e) => {
      ripplesRef.current.push({ x: e.clientX, y: e.clientY, born: performance.now() });
    };

    const handleResize = () => setSize();

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('click', handleClick);
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('click', handleClick);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@200;300;400;500;600;700;800&family=JetBrains+Mono:wght@300;400&display=swap');

        * { margin: 0; padding: 0; box-sizing: border-box; font-family: 'Manrope', -apple-system, system-ui, sans-serif; -webkit-font-smoothing: antialiased; }
        html, body, #root { height: 100%; width: 100%; overflow: hidden; background: #000510; color: white; }

        .stage { position: relative; width: 100vw; height: 100vh; overflow: hidden; }
        #bgCanvas { position: absolute; inset: 0; pointer-events: none; z-index: 1; }
        .vignette { position: absolute; inset: 0; background: radial-gradient(ellipse 70% 80% at 50% 50%, rgba(0,5,16,0.5) 0%, rgba(0,5,16,0) 70%); pointer-events: none; z-index: 2; }

        .content {
          position: relative; z-index: 5;
          width: 100%; height: 100%;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          gap: 20px; padding: 44px 24px;
        }

        .logo-wrap {
          position: relative; width: 240px; height: 120px;
          opacity: 0;
          animation: fadeInUp 1.4s cubic-bezier(0.16, 1, 0.3, 1) 0.3s forwards;
          flex-shrink: 0;
        }

        .logo-perspective { perspective: 1400px; perspective-origin: 50% 50%; width: 100%; height: 100%; }
        .logo-tilt { width: 100%; height: 100%; transform-style: preserve-3d; transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1); }
        .logo-bob { width: 100%; height: 100%; transform-style: preserve-3d; animation: gentleBob 5.5s ease-in-out infinite; }
        .logo-rotate { position: relative; width: 100%; height: 100%; transform-style: preserve-3d; animation: continuousRotate 16s linear infinite; }
        .face { position: absolute; top: 50%; left: 50%; backface-visibility: hidden; -webkit-backface-visibility: hidden; }

        .face-front, .face-back {
          width: 240px; height: 120px; border-radius: 18px;
          display: flex; align-items: center; justify-content: center; overflow: hidden;
          background:
            radial-gradient(ellipse 120% 80% at 50% -20%, rgba(255,255,255,0.28) 0%, transparent 55%),
            radial-gradient(ellipse 100% 60% at 50% 120%, rgba(230, 98, 8, 0.18) 0%, transparent 60%),
            linear-gradient(135deg, rgba(40, 70, 105, 0.7) 0%, rgba(10, 25, 45, 0.95) 50%, rgba(3, 12, 24, 1) 100%),
            #061426;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -1px 0 rgba(0,0,0,0.6), inset 0 0 60px rgba(0, 57, 99, 0.3);
        }
        .face-front { transform: translate(-50%, -50%) translateZ(11px); }
        .face-back { transform: translate(-50%, -50%) rotateY(180deg) translateZ(11px); }

        .face-front::before, .face-back::before {
          content: ''; position: absolute; inset: 0;
          background: linear-gradient(115deg, transparent 15%, rgba(255,255,255,0.18) 42%, rgba(255,255,255,0.32) 50%, rgba(230, 98, 8, 0.12) 54%, rgba(150, 200, 255, 0.15) 58%, transparent 85%);
          pointer-events: none; border-radius: inherit; mix-blend-mode: screen;
        }
        .face-front::after, .face-back::after {
          content: ''; position: absolute; top: 1px; left: 8%; right: 8%; height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent);
          pointer-events: none;
        }

        .face-right, .face-left {
          width: 22px; height: 120px;
          background: linear-gradient(90deg, #02081a 0%, #0a1a2e 25%, #1a3050 50%, #0a1a2e 75%, #02081a 100%);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.15), inset 0 -1px 0 rgba(0,0,0,0.7);
        }
        .face-right { transform: translate(-50%, -50%) rotateY(90deg) translateZ(120px); }
        .face-left { transform: translate(-50%, -50%) rotateY(-90deg) translateZ(120px); }

        .face-top, .face-bottom {
          width: 240px; height: 22px;
          background: linear-gradient(180deg, #02081a 0%, #0a1a2e 25%, #1a3050 50%, #0a1a2e 75%, #02081a 100%);
          box-shadow: inset 1px 0 0 rgba(255,255,255,0.1), inset -1px 0 0 rgba(0,0,0,0.5);
        }
        .face-top { transform: translate(-50%, -50%) rotateX(90deg) translateZ(60px); }
        .face-bottom { transform: translate(-50%, -50%) rotateX(-90deg) translateZ(60px); }

        .logo-ground-shadow {
          position: absolute; bottom: -32px; left: 50%; width: 260px; height: 26px;
          background: radial-gradient(ellipse, rgba(0, 30, 54, 0.85) 0%, rgba(230, 98, 8, 0.15) 30%, rgba(0, 57, 99, 0.4) 50%, transparent 75%);
          filter: blur(20px); animation: shadowPulse 5.5s ease-in-out infinite;
          pointer-events: none;
        }

        .logo-halo {
          position: absolute; top: 50%; left: 50%; width: 380px; height: 380px;
          background: conic-gradient(from 0deg, rgba(230, 98, 8, 0.18), rgba(80, 140, 220, 0.15), rgba(150, 100, 220, 0.12), rgba(230, 98, 8, 0.18));
          border-radius: 50%; filter: blur(50px);
          animation: haloRotate 20s linear infinite;
          pointer-events: none; z-index: -1;
        }

        .hero-text { text-align: center; flex-shrink: 0; }

        .heading {
          font-size: clamp(34px, 6vw, 68px);
          font-weight: 600; letter-spacing: -0.05em; line-height: 1;
          color: #ffffff;
          background-image: linear-gradient(180deg, #ffffff 0%, #b8c5d6 100%);
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent;
          user-select: none; margin-bottom: 14px;
          text-align: center; white-space: nowrap;
          opacity: 0; transform: translateY(30px);
          animation: headingShow 1.4s cubic-bezier(0.16, 1, 0.3, 1) 0.55s forwards;
          position: relative;
        }

        .heading-glow {
          position: absolute; inset: 0;
          font-size: inherit; font-weight: inherit;
          letter-spacing: inherit; line-height: inherit;
          color: rgba(230, 98, 8, 0.45);
          -webkit-text-fill-color: rgba(230, 98, 8, 0.45);
          filter: blur(40px);
          pointer-events: none; z-index: -1; user-select: none;
        }

        @keyframes headingShow { to { opacity: 1; transform: translateY(0); } }

        .tagline {
          font-size: 14px; color: #94a3b8; font-weight: 300; letter-spacing: 0.04em;
          opacity: 0;
          animation: fadeInUp 1.4s cubic-bezier(0.16, 1, 0.3, 1) 0.8s forwards;
        }

        .card {
          position: relative; width: 100%; max-width: 420px;
          padding: 32px 36px 28px; border-radius: 22px;
          background: linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%);
          backdrop-filter: blur(28px) saturate(200%);
          -webkit-backdrop-filter: blur(28px) saturate(200%);
          border: 1px solid rgba(255,255,255,0.1);
          box-shadow: 0 30px 80px -20px rgba(0, 8, 20, 0.9), 0 0 60px rgba(0, 30, 54, 0.4), inset 0 1px 0 rgba(255,255,255,0.1);
          animation: pulseGlow 6s ease-in-out infinite, fadeInUp 1.4s cubic-bezier(0.16, 1, 0.3, 1) 1.05s both;
          overflow: hidden; flex-shrink: 0;
        }

        .card::before {
          content: ''; position: absolute; inset: 0; border-radius: inherit;
          background: linear-gradient(135deg, rgba(255,255,255,0.18) 0%, transparent 30%, transparent 70%, rgba(230, 98, 8, 0.08) 100%);
          pointer-events: none; mix-blend-mode: overlay;
        }

        .card::after {
          content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent);
          pointer-events: none;
        }

        .card-scan {
          position: absolute; top: 0; bottom: 0; width: 80px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.07), transparent);
          animation: scanLine 8s ease-in-out infinite;
          pointer-events: none; mix-blend-mode: overlay;
        }

        .card-header { text-align: center; margin-bottom: 22px; }
        .card-header h2 { font-size: 17px; font-weight: 500; color: white; letter-spacing: -0.01em; }
        .card-header p { margin-top: 5px; font-size: 12px; color: #94a3b8; font-weight: 300; }

        .ms-btn {
          position: relative; width: 100%; height: 52px;
          border-radius: 12px; border: none; cursor: pointer;
          background: linear-gradient(180deg, #ffffff 0%, #f5f5f8 100%);
          color: #1a1a1a; font-size: 14px; font-weight: 500;
          font-family: 'Manrope', sans-serif;
          display: flex; align-items: center; justify-content: center; gap: 12px;
          overflow: hidden;
          box-shadow: 0 1px 2px rgba(0,0,0,0.04), 0 12px 36px -10px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,1);
          transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          will-change: transform;
        }

        .ms-btn::before {
          content: ''; position: absolute; inset: -2px;
          background: linear-gradient(135deg, #E66208, #00A4EF, #7FBA00, #FFB900, #F25022, #E66208);
          background-size: 300% 300%; border-radius: inherit;
          z-index: -1; opacity: 0; filter: blur(10px);
          transition: opacity 0.4s ease;
          animation: holoBorder 4s linear infinite;
        }

        .ms-btn::after {
          content: ''; position: absolute; top: -50%; left: 0;
          width: 50%; height: 200%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent);
          transform: translateX(-130%) skewX(-20deg);
          pointer-events: none;
          transition: transform 0.9s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .ms-btn:hover {
          box-shadow: 0 4px 8px rgba(0,0,0,0.08), 0 24px 48px -10px rgba(0,0,0,0.5), 0 0 0 2px rgba(230, 98, 8, 0.3), inset 0 1px 0 rgba(255,255,255,1);
        }
        .ms-btn:hover::before { opacity: 0.6; }
        .ms-btn:hover::after { transform: translateX(220%) skewX(-20deg); }
        .ms-btn:active { transform: scale(0.97) !important; }

        .ms-btn .arrow {
          width: 14px; height: 14px; opacity: 0.5;
          transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .ms-btn:hover .arrow { transform: translateX(6px); }

        .hint-row {
          margin-top: 18px;
          display: flex; align-items: center; justify-content: center; gap: 6px;
          font-size: 11px; color: #6b7585; font-weight: 300; letter-spacing: 0.02em;
        }
        .hint-row strong { color: #94a3b8; font-weight: 400; }
        .hint-row svg { width: 11px; height: 11px; }

        .divider-row { margin-top: 20px; display: flex; align-items: center; gap: 12px; }
        .divider-row .line { flex: 1; height: 1px; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent); }
        .divider-row .label {
          font-size: 9px; letter-spacing: 0.3em; text-transform: uppercase;
          color: #4a5263; font-family: 'JetBrains Mono', monospace;
        }

        .help-link { margin-top: 14px; text-align: center; }
        .help-link a {
          font-size: 11px; color: #6b7585; text-decoration: none;
          font-weight: 300; transition: color 0.3s;
        }
        .help-link a:hover { color: #cbd5e1; }

        .footer-wrap {
          position: absolute; bottom: 24px; left: 0; right: 0;
          z-index: 5; text-align: center;
          opacity: 0;
          animation: fadeIn 1.6s ease 1.7s forwards;
        }
        .footer-wrap .divider { width: 36px; height: 1px; background: rgba(255,255,255,0.15); margin: 0 auto 10px; }
        .footer-wrap .links {
          display: flex; align-items: center; justify-content: center; gap: 14px;
          font-size: 10px; color: #4a5263; font-weight: 300; letter-spacing: 0.02em;
        }
        .footer-wrap .links a { color: inherit; text-decoration: none; transition: color 0.3s; }
        .footer-wrap .links a:hover { color: #94a3b8; }
        .footer-wrap .links span.dot { color: #2a3340; }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes continuousRotate { from { transform: rotateY(0deg); } to { transform: rotateY(360deg); } }
        @keyframes gentleBob { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-12px); } }
        @keyframes shadowPulse {
          0%, 100% { opacity: 0.7; transform: translateX(-50%) scaleX(1); }
          50% { opacity: 0.4; transform: translateX(-50%) scaleX(0.8); }
        }
        @keyframes haloRotate {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
        @keyframes scanLine { 0% { transform: translateX(-100%); } 100% { transform: translateX(900%); } }
        @keyframes holoBorder { 0% { background-position: 0% 50%; } 100% { background-position: 300% 50%; } }
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 30px 80px -20px rgba(0, 8, 20, 0.9), 0 0 60px rgba(0, 30, 54, 0.4), inset 0 1px 0 rgba(255,255,255,0.1); }
          50% { box-shadow: 0 30px 80px -20px rgba(0, 8, 20, 0.9), 0 0 100px rgba(0, 57, 99, 0.6), inset 0 1px 0 rgba(255,255,255,0.14); }
        }

        @media (max-height: 750px) {
          .content { gap: 20px; padding: 30px 24px 80px; }
          .logo-wrap { width: 240px; height: 120px; }
          .face-front, .face-back { width: 240px; height: 120px; border-radius: 16px; }
          .face-right, .face-left { height: 120px; }
          .face-right { transform: translate(-50%, -50%) rotateY(90deg) translateZ(120px); }
          .face-left { transform: translate(-50%, -50%) rotateY(-90deg) translateZ(120px); }
          .face-top, .face-bottom { width: 240px; }
          .face-top { transform: translate(-50%, -50%) rotateX(90deg) translateZ(60px); }
          .face-bottom { transform: translate(-50%, -50%) rotateX(-90deg) translateZ(60px); }
          .heading { font-size: clamp(36px, 6.5vw, 68px); }
          .card { padding: 26px 30px 24px; }
        }
      `}</style>

      <div className="stage">
        <canvas ref={canvasRef} id="bgCanvas" />
        <div className="vignette" />

        {(authState === 'checking' || authState === 'signing-out') && (
          <div style={{
            position:'fixed',
            inset:0,
            display:'flex',
            flexDirection:'column',
            alignItems:'center',
            justifyContent:'center',
            zIndex:1000,
            gap:24
          }}>
            {/* Animated RC Smart Support logo orb */}
            <div style={{
              position:'relative',
              width:80,height:80,
            }}>
              <div style={{
                position:'absolute',
                inset:0,
                borderRadius:'50%',
                background:'radial-gradient(circle, rgba(124,58,237,0.4) 0%, rgba(8,145,178,0.2) 50%, transparent 70%)',
                animation:'pulseGlow 2s ease-in-out infinite',
              }}/>
              <div style={{
                position:'absolute',
                inset:8,
                borderRadius:'50%',
                background:'linear-gradient(135deg,#7c3aed 0%,#0891b2 50%,#06b6d4 100%)',
                display:'flex',alignItems:'center',justifyContent:'center',
                boxShadow:'0 12px 36px rgba(124,58,237,0.4), inset 0 2px 0 rgba(255,255,255,0.3)',
                animation:'orbFloat 3s ease-in-out infinite',
              }}>
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="10" rx="2" ry="2"/>
                  <circle cx="12" cy="5" r="2"/>
                  <path d="M12 7v4M8 16h.01M16 16h.01"/>
                </svg>
              </div>
            </div>
            <div style={{
              fontSize:18,fontWeight:700,letterSpacing:'-0.01em',
              background:'linear-gradient(135deg,#a78bfa 0%,#22d3ee 100%)',
              WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',
              backgroundClip:'text',
            }}>RC Smart Support</div>
            <div style={{color:'rgba(187,213,255,0.5)',fontSize:11,letterSpacing:'0.1em',textTransform:'uppercase',fontWeight:600}}>
              {authState === 'signing-out' ? 'Signing out' : 'Loading'}
            </div>
            <style>{`
              @keyframes spin{to{transform:rotate(360deg)}}
              @keyframes pulseGlow{
                0%,100%{transform:scale(1);opacity:0.6}
                50%{transform:scale(1.15);opacity:1}
              }
              @keyframes orbFloat{
                0%,100%{transform:translateY(0)}
                50%{transform:translateY(-4px)}
              }
            `}</style>
          </div>
        )}

        <div className="content" style={{visibility: (authState === 'checking' || authState === 'signing-out') ? 'hidden' : 'visible'}}>

          <div className="logo-wrap">
            <div className="logo-halo" />
            <div className="logo-ground-shadow" />
            <div className="logo-perspective">
              <div className="logo-tilt" ref={logoTiltRef}>
                <div className="logo-bob">
                  <div className="logo-rotate">
                    <div className="face face-front"><RoyalCyberLogo /></div>
                    <div className="face face-back"><RoyalCyberLogo /></div>
                    <div className="face face-right" />
                    <div className="face face-left" />
                    <div className="face face-top" />
                    <div className="face face-bottom" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="hero-text">
            <h1 className="heading">
              <span className="heading-glow" aria-hidden="true">RC Smart Support</span>
              RC Smart Support
            </h1>
            <p className="tagline">Where IT Meets Intelligence</p>
          </div>

          <div className="card">
            <div className="card-scan" />

            <div className="card-header">
              <h2>Welcome back</h2>
              <p>Sign in to continue to your workspace</p>
            </div>

            <button
              className="ms-btn"
              ref={btnRef}
              onClick={handleSignIn}
              disabled={authState !== 'ready'}
              style={{cursor: authState === 'ready' ? 'pointer' : 'wait', opacity: authState === 'ready' ? 1 : 0.7}}
            >
              <svg width="18" height="18" viewBox="0 0 21 21">
                <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
              </svg>
              <span>
                {authState === 'checking' ? 'Checking session…' :
                 authState === 'signing-in' ? 'Redirecting to Microsoft…' :
                 'Sign in with Microsoft'}
              </span>
              <svg className="arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
            {errorMsg && (
              <div style={{marginTop:10,padding:'8px 12px',background:'rgba(220,38,38,0.15)',border:'1px solid rgba(220,38,38,0.3)',borderRadius:8,color:'#fca5a5',fontSize:12}}>
                {errorMsg}
              </div>
            )}

            <div className="hint-row">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
              <span>Use your <strong>@royalcyber.com</strong> account</span>
            </div>

            <div className="divider-row">
              <div className="line" />
              <div className="label">SSO · SAML 2.0</div>
              <div className="line" />
            </div>

            <div className="help-link">
              <a href="#">Trouble signing in?</a>
            </div>
          </div>
        </div>

        <div className="footer-wrap">
          <div className="divider" />
          <div className="links">
            <span>© 2026 Royal Cyber Inc.</span>
            <span className="dot">·</span>
            <a href="#">Privacy</a>
            <span className="dot">·</span>
            <a href="#">Terms</a>
            <span className="dot">·</span>
            <a href="#">Support</a>
          </div>
        </div>
      </div>
    </>
  );
}

function RoyalCyberLogo() {
  return (
    <svg width="220" height="64" viewBox="0 0 270 78" xmlns="http://www.w3.org/2000/svg"
      style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.5))' }}
    >
      <defs>
        <linearGradient id="navyGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.25" />
          <stop offset="50%" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.4" />
        </linearGradient>
        <linearGradient id="whiteGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
          <stop offset="100%" stopColor="#d8dee8" stopOpacity="1" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="138" height="78" rx="6" fill="#001E36" />
      <rect x="0" y="0" width="138" height="78" rx="6" fill="url(#navyGrad)" />
      <text x="69" y="50" textAnchor="middle" fill="#FFFFFF" fontFamily="Manrope, sans-serif" fontSize="26" fontWeight="800" letterSpacing="1.8">ROYAL</text>
      <rect x="142" y="0" width="128" height="78" rx="6" fill="url(#whiteGrad)" />
      <text x="206" y="50" textAnchor="middle" fill="#001E36" fontFamily="Manrope, sans-serif" fontSize="26" fontWeight="800" letterSpacing="1.8">CYBER</text>
    </svg>
  );
}
